import React from "react";
// Billing Determinant Report page

// Default
import { useState, useEffect } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import PeakUsageChart from "../charts/PeakUsageChart";
import DemandChargesChart from "../charts/DemandChargesChart";
import CustomDataTable from "../table/CustomTable";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";

const BillingDeterminantReport: React.FC = () => {
  const [dataArray, setDataArray] = useState<any[]>([]);
  const [chartData, setChartData] = useState({
    peakUsage: [],
    offPeakUsage: [],
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const downloadCSV = () => {
    const headers = Object.keys(dataArray[0]).join(",");
    const rows = dataArray
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const COLUMNS = [
    "Timestamp",
    "Peak Usage",
    "Off-Peak Usage",
    "Demand Charges",
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/30d33fa0-bb60-11ef-85d5-e5b1b2e5df91/values/timeseries?keys=IP_KWH,EventCode,EventDescription&startTs=1730485800000&endTs=1736015340000&intervalType=WEEK&agg=MIN`,
          {}
        );

        const dataArray = Object.keys(response).flatMap((key) =>
          (response as any)[key].map((item: any) => ({
            ts: item.ts,
            [key]: item.value,
          }))
        );

        const mergedData = dataArray.reduce((acc, item) => {
          const existing = acc.find((i: any) => i.ts === item.ts);
          if (existing) {
            Object.assign(existing, item);
          } else {
            acc.push(item);
          }
          return acc;
        }, []);

        setDataArray(mergedData);

        const peakUsage = mergedData.reduce(
          (max: number, item: any) =>
            Math.max(max, parseFloat(item.IP_KWH || 0)),
          0
        );
        const offPeakUsage = mergedData.reduce(
          (min: number, item: any) =>
            Math.min(min, parseFloat(item.IP_KWH || Infinity)),
          Infinity
        );

        setChartData({ peakUsage, offPeakUsage });
      } catch (error) {
        return error;
      }
    };

    fetchData();
  }, []);

  const rowsContent = dataArray?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {formatDate(new Date(item?.ts), "DD/MM/YYYY HH:mm:ss")}{" "}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {chartData?.peakUsage}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {chartData?.offPeakUsage}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">500</Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card className="meters_title_card">
        <Typography variant="h5">Billing Determinant Report</Typography>
      </Card>

      <Grid container spacing={2.25}>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card sx={{ mt: "20px", maxHeight: "400px" }}>
            <CardHeader
              title={<Typography variant="h5">Peak/Off Peak Usage</Typography>}
            />
            <Divider />

            <CardContent>
              <PeakUsageChart dataArray={dataArray} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card sx={{ mt: "20px", maxHeight: "400px" }}>
            <CardHeader
              title={<Typography variant="h5">Demand Charges</Typography>}
            />
            <Divider />

            <CardContent>
              <DemandChargesChart />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={downloadCSV}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent sx={{ overflow: "auto", maxHeight: "600px" }}>
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={dataArray?.length}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default BillingDeterminantReport;
